import { type ReactNode } from 'react';
import { Focusable, TooltipTrigger } from 'react-aria-components';

import { Box, Group, SkeletonRect, Text, Tooltip } from '@ekhodealer/ekho-common/rev';

import { CopyableButton } from '@common/components/CopyableButton';
import Info16 from '@common/components/icons/Info16';
import { TextCell } from '@common/components/TextCell';

interface InfoLineItemProps {
	/** The label/title for the info line */
	title: string;
	/** The value to display */
	value?: string;
	/** The content to display */
	content?: ReactNode;
	/** Optional tooltip content to display with an info icon next to the title */
	tooltip?: string;
	/** If true, displays the value as a ghost button that copies to clipboard on click */
	isCopyable?: boolean;
	/** If true, shows a skeleton in place of the value */
	isLoading?: boolean;
}

interface TooltipIconProps {
	tooltip: string;
}

const TooltipIcon = ({ tooltip }: TooltipIconProps) => {
	return (
		<TooltipTrigger delay={60}>
			<Focusable>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						cursor: 'help',
					}}>
					<Info16 />
				</div>
			</Focusable>
			<Tooltip>{tooltip}</Tooltip>
		</TooltipTrigger>
	);
};

export interface TitleSectionProps {
	title: string;
	tooltip?: string;
}

export const TitleSection = ({ title, tooltip }: TitleSectionProps) => {
	return (
		<Group itemsSpacing='4' itemsAlignY='center' noWrap>
			<Group.Item>
				<Text size='footnote'>{title}</Text>
			</Group.Item>
			{tooltip && (
				<Group.Item>
					<TooltipIcon tooltip={tooltip} />
				</Group.Item>
			)}
		</Group>
	);
};

interface ContentSectionProps {
	value?: string;
	content?: ReactNode;
	isCopyable: boolean;
	title: string;
	isLoading?: boolean;
}

const ContentSection = ({ value, content, isCopyable, isLoading }: ContentSectionProps) => {
	// Show skeleton when loading
	if (isLoading) {
		return (
			<Box padding={{ left: 10, top: 4 }}>
				<SkeletonRect width='150px' height='16px' />
			</Box>
		);
	}

	if (isCopyable && value) {
		return <CopyableButton value={value} />;
	}

	// This extra padding is to align the static content with the copyable button
	const staticContent = content ? <>{content}</> : <TextCell value={value} noWrap={false} />;
	return (
		<Box padding={{ left: 10, top: 4 }} rounding='0'>
			{staticContent}
		</Box>
	);
};

/**
 * InfoLineItem component that displays a label-value pair
 * with consistent spacing and alignment
 * Optionally displays an info icon with tooltip next to the title
 * Optionally displays the value as a copyable button
 */
export const InfoLineItem = ({
	title,
	value,
	content,
	tooltip,
	isCopyable = false,
	isLoading = false,
}: InfoLineItemProps) => {
	return (
		<Group itemsAlignX='space-between' itemsAlignY='center' noWrap>
			<Group.Item grow={1}>
				<Group itemsSpacing='4' itemsAlignY='center' noWrap>
					<Group.Item>
						<TitleSection title={title} tooltip={tooltip} />
					</Group.Item>
				</Group>
			</Group.Item>
			<Group.Item grow={2}>
				<ContentSection
					value={value}
					content={content}
					isCopyable={isCopyable}
					title={title}
					isLoading={isLoading}
				/>
			</Group.Item>
		</Group>
	);
};
